import type { OpenWorkTabTitleDeclaration } from '../plugin-system/shell-contribution-types.ts';

const MAX_TITLE_PART_CHARACTERS = 300;
const MAX_TITLE_CONTEXT_PARTS = 4;

export interface OpenWorkTabContext {
  name: string | null;
  reference: string | null;
}

export interface OpenWorkTabTitle {
  name: string | null;
  kind: string | null;
  context: readonly OpenWorkTabContext[];
}

export interface DisplayedOpenWorkTabTitle {
  label: string;
  context: readonly string[];
}

function titlePart(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.slice(0, MAX_TITLE_PART_CHARACTERS).replaceAll(/\s+/g, ' ').trim();
  return text || null;
}

function contextPart(value: unknown): OpenWorkTabContext | null {
  if (!value || typeof value !== 'object') return null;
  const declaration = value as Record<string, unknown>;
  const name = titlePart(declaration.name);
  const reference = titlePart(declaration.reference);
  return name || reference ? { name, reference } : null;
}

export function sanitizeOpenWorkTabTitle(value: unknown): OpenWorkTabTitle | null {
  if (!value || typeof value !== 'object') return null;
  const declaration = value as OpenWorkTabTitleDeclaration;
  const name = titlePart(declaration.name);
  const kind = titlePart(declaration.kind);
  if (!name && !kind) return null;
  const context = Array.isArray(declaration.context)
    ? declaration.context
        .map(contextPart)
        .filter((part): part is OpenWorkTabContext => part !== null)
        .slice(0, MAX_TITLE_CONTEXT_PARTS)
    : [];
  return { name, kind, context };
}

export const UNNAMED_OPEN_WORK_TAB_TITLE: OpenWorkTabTitle = Object.freeze({
  name: 'Open page',
  kind: null,
  context: Object.freeze([]),
});

// Pages that declare no title are named from their header or navigation label.
export function namedOpenWorkTabTitle(name: string): OpenWorkTabTitle {
  return sanitizeOpenWorkTabTitle({ name }) ?? UNNAMED_OPEN_WORK_TAB_TITLE;
}

export function openWorkTabLabel({
  name,
  kind,
}: Pick<OpenWorkTabTitleDeclaration, 'name' | 'kind'>): string {
  return [name, kind].filter(Boolean).join(' ');
}

// "Kedia quiet title (M16)": a reference stays beside the name it identifies.
export function referencedOpenWorkTabContext({ name, reference }: OpenWorkTabContext): string {
  return name && reference ? `${name} (${reference})` : (name ?? reference ?? '');
}

export function openWorkTabTitleKey(title: OpenWorkTabTitle): string {
  return JSON.stringify([title.name, title.kind, title.context]);
}

export function distinguishedOpenWorkTabLabel({ label, context }: DisplayedOpenWorkTabTitle) {
  return [label, ...context].join(' · ');
}

function distinguishes(parts: readonly (readonly string[])[]): boolean {
  const keys = parts.map((part) => part.join('\u001f').toLocaleLowerCase());
  return new Set(keys).size === keys.length;
}

// The fewest context pieces, closest first, that tell every tab in the group
// apart; null when no depth does.
function distinguishingContext(
  pieces: readonly (readonly string[])[],
): (readonly string[])[] | null {
  const deepest = Math.max(...pieces.map((parts) => parts.length));
  for (let depth = 1; depth <= deepest; depth += 1) {
    const shown = pieces.map((parts) => parts.slice(0, depth).filter(Boolean));
    if (distinguishes(shown)) return shown;
  }
  return null;
}

// Tabs that read the same each add the fewest context pieces that set them
// apart, so a lone tab stays short. Names come first; record references join
// their pieces only when no names a person would recognize tell tabs apart.
export function displayedOpenWorkTabTitles(
  titles: readonly OpenWorkTabTitle[],
): DisplayedOpenWorkTabTitle[] {
  const labels = titles.map(openWorkTabLabel);
  const sameLabelGroups = new Map<string, number[]>();
  labels.forEach((label, index) => {
    const key = label.toLocaleLowerCase();
    sameLabelGroups.set(key, [...(sameLabelGroups.get(key) ?? []), index]);
  });
  const shownContext: (readonly string[])[] = titles.map(() => []);
  for (const members of sameLabelGroups.values()) {
    if (members.length < 2) continue;
    const contexts = members.map((index) => titles[index]!.context);
    const shown =
      distinguishingContext(contexts.map((context) => context.map((piece) => piece.name ?? ''))) ??
      distinguishingContext(contexts.map((context) => context.map(referencedOpenWorkTabContext))) ??
      contexts.map((context) => context.map(referencedOpenWorkTabContext));
    members.forEach((index, position) => {
      shownContext[index] = shown[position]!;
    });
  }
  return labels.map((label, index) => ({ label, context: shownContext[index]! }));
}
